import { API_BASE_URL } from 'lib/config/apiEndpoints';

const INVALID_US_ZIP_CODES = ['00000', '99999'];

export type BloodVisionModality = 'walk_in' | 'at_home';

export type SlotAvailabilityWalkIn = {
  labs_nearby: number;
  labs_checked: number;
  first_bookable_lab: string | null;
};

export type SlotAvailability = {
  modality: BloodVisionModality;
  zip_code: string;
  city?: string;
  state?: string;
  serviceable: boolean;
  slots_available: boolean;
  days_with_slots: number;
  open_slots: number;
  // Dropped by the API when there are no slots (nil fields are omitted).
  earliest_date?: string | null;
  // at_home only: true when checked against the ZIP-area placeholder rather than
  // an exact street.
  approximate?: boolean;
  walk_in?: SlotAvailabilityWalkIn;
};

type SlotAvailabilityApiResponse = {
  data: SlotAvailability | null;
  // Success uses `status: "ok"`; errors use `status: "error"` with a nested
  // `{ message }` object (a legacy string shape is tolerated defensively).
  error?: { message?: string } | string | null;
  status: string;
};

export type CheckSlotAvailabilityParams = {
  zipCode: string;
  modality: BloodVisionModality;
  street?: string;
};

export type CheckSlotAvailabilityResult = {
  ok: boolean;
  status: number;
  slot?: SlotAvailability;
  error?: string;
};

const SLOT_AVAILABILITY_STATUS_FALLBACK_MESSAGES: Record<number, string> = {
  400: 'Missing required parameters. Please enter a ZIP code and try again.',
  403: 'Slot availability checks are not available here.',
  422: 'That modality is not supported. Please try again.',
  429: 'Too many requests — please wait a moment and try again.',
  502: 'We had trouble reaching our scheduling partner. Please try again shortly.',
  503: 'Slot availability checks are temporarily unavailable. Please try again later.',
};

export const checkSlotAvailability = async ({
  zipCode,
  modality,
  street,
}: CheckSlotAvailabilityParams): Promise<CheckSlotAvailabilityResult> => {
  const urlToFetch = new URL(
    `${API_BASE_URL}/api/web_v1/blood_vision/slot_availability`,
  );

  urlToFetch.searchParams.append('zip_code', zipCode);
  urlToFetch.searchParams.append('modality', modality);

  if (modality === 'at_home' && street) {
    urlToFetch.searchParams.append('street', street);
  }

  try {
    const response = await fetch(urlToFetch.href, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
      },
    });

    const data: SlotAvailabilityApiResponse | null = await response
      .json()
      .catch(() => null);

    if (response.status === 200 && data?.data) {
      return { ok: true, status: response.status, slot: data.data };
    }

    const fallbackMessage =
      SLOT_AVAILABILITY_STATUS_FALLBACK_MESSAGES[response.status] ??
      'Something went wrong while checking slot availability. Please try again.';

    // Errors come back as `{ error: { message } }`; tolerate a plain string too.
    const backendMessage =
      typeof data?.error === 'string' ? data.error : data?.error?.message;

    return {
      ok: false,
      status: response.status,
      error: backendMessage || fallbackMessage,
    };
  } catch (error) {
    return {
      ok: false,
      status: 0,
      error: 'An error occurred while checking slot availability.',
    };
  }
};

type ServicabilityValidationResult = {
  zip?: string;
  error?: string;
  valid: boolean;
  serviceabilityText?: string;
  availableModalities?: string[];
};

export const servicabilityValidation = async (
  zip: string,
): Promise<ServicabilityValidationResult> => {
  const op: ServicabilityValidationResult = {
    zip: undefined,
    error: undefined,
    valid: false,
  };

  if (zip.length !== 5 || INVALID_US_ZIP_CODES.includes(zip)) {
    op.error = 'Invalid ZIP code. Please enter a valid US ZIP code.';
    op.valid = false;
    return op;
  }

  try {
    // Call geo_data API to validate the ZIP code
    const urlToFetch = new URL(
      `${API_BASE_URL}/api/web_v1/blood_vision/serviceability_status`,
    );

    if (zip) {
      urlToFetch.searchParams.append('zip_code', zip as string);
    }

    const response = await fetch(urlToFetch.href, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
      },
    });
    console.log('response from servicability API ::::', response.status);

    // If the response is not ok, the ZIP code is not valid
    if (!(response.status === 200)) {
      op.error = 'Invalid ZIP code. Please enter a valid US ZIP code.';
      op.valid = false;
      return op;
    }

    // If the response is ok, parse the JSON
    const data = await response.json();

    // Check if the response contains serviceability information
    if (data && data.data) {
      if (data.data.is_serviceable) {
        op.zip = zip;
        op.valid = true;
        op.serviceabilityText = data.data.serviceability_text;
        op.availableModalities = data.data.available_modalities;
      } else {
        op.error = data.data.serviceability_text;
        op.valid = false;
      }
    } else {
      op.error = 'Invalid response from server.';
      op.valid = false;
    }
  } catch (error) {
    console.error('Error validating ZIP code:', error);
    op.error = 'An error occurred while validating the ZIP code.';
    op.valid = false;
  }

  console.log('response from servicability API ::::', op);

  return op;
};
